import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { BrandsEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandsEndPointLogic";
import {
  setBrandPropertiesLoader,
  setIsFetchedPopupData,
} from "../../../../../features/brandsSlice";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import {
  isValidParam,
  QUERY_IDS,
} from "../utilitiesOfBrandConfig/helperOfBrandConfig";
import { useBrands } from "../utils/useBrands";

// Brands-owned fork of brandPropertiesHook/useBrandProperties.ts. Same
// formatting/validation logic and API request shapes, but:
//  - calls BrandsEndPointLogic instead of BrandConfigReportEndPointLogic
//  - dispatches into brandsSlice instead of brandConfigurationReportSlice
//  - doesn't re-fetch the whole legacy brand configuration report just to
//    look up a brand's display name — BrandProperties.tsx derives that from
//    allBrandsData (already fetched by this module) instead
//  - refreshes via the Brands module's own fetchedBrandsData() after a
//    successful "Continue Setup" instead of the legacy report/PIE-chart refresh
export const useBrandProperties = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const { fetchedBrandsData } = useBrands();
  const type = "site";

  const brandSharedUid = searchParams.get("brandSharedUid") as any;
  const bkInterfaceSharedUid = searchParams.get("bkInterfaceSharedUid") as any;

  const { brandPropertiesData, brandBackendRequirementsData } = useSelector(
    (state: any) => state.brands,
  );

  // Extract and sort requirements
  const requirements = brandBackendRequirementsData?.[0]?.requirements || [];
  const sortedRequirements = [...requirements].sort(
    (a, b) => (a?.order || 0) - (b?.order || 0),
  );

  const keeperLabels = brandPropertiesData?.[0]?.keeper?.labels || [];
  const brandObjects = brandPropertiesData?.[0]?.objects || [];

  // Helper: Extract placeholder value for key type = pattern
  const extractPlaceholderValue = (
    defaultValue: string | any,
    fieldValue: string | any,
  ) => {
    try {
      const defaultObj = JSON.parse(defaultValue);
      const fieldObj = JSON.parse(fieldValue);

      // Find placeholder key
      const placeholderKey = Object.keys(defaultObj).find(
        (key) =>
          typeof defaultObj[key] === "string" &&
          defaultObj[key].includes("$$placeholder"),
      );

      if (!placeholderKey) return null;

      return {
        placeholderKey,
        extractedValue: fieldObj[placeholderKey],
      };
    } catch {
      return null;
    }
  };

  // Helper: Find matching data for requirement
  const findMatchingData = (requirement: any) => {
    const { property_target, key_name } = requirement;

    if (property_target === "keeper") {
      return keeperLabels.find((label: any) => label.label_name === key_name);
    }

    if (
      property_target === "brand_id" ||
      property_target === "raw_output_table_name"
    ) {
      return brandObjects.find(
        (obj: any) =>
          obj.field_type === key_name && obj.object_type === property_target,
      );
    }

    return null;
  };

  // Map requirements to formatted properties
  const formattedBrandProperties = sortedRequirements
    .map((requirement) => {
      const matchedfiledValue = findMatchingData(requirement);

      const {
        key_title,
        key_name,
        key_type,
        requirement_type,
        property_target,
        description,
        default_value,
      } = requirement;

      // Skip rows that have no value to display
      if (!key_name && !key_title && !key_type) return null;

      // Get value based on property target
      const label_value = matchedfiledValue?.label_value ?? null;
      const field_value = matchedfiledValue?.field_value ?? null;
      let value = null;
      let placeholder = null;
      let placeholderKey = null;

      if (key_type === "user_input" || key_type === "changeable") {
        value = label_value || field_value || default_value || "";
      }

      // Handle pattern type
      if (key_type === "pattern") {
        const hasValue = (val: any) =>
          val !== null && val !== "" && val !== undefined;
        const parseIfString = (val: string | any) =>
          typeof val === "string" ? JSON.parse(val) : val;

        const findPlaceholderKey = (obj: Record<string, any>) =>
          Object.keys(obj).find(
            (k) =>
              typeof obj[k] === "string" && obj[k].includes("$$placeholder$$"),
          );
        if (hasValue(default_value) && hasValue(field_value)) {
          const extracted = extractPlaceholderValue(default_value, field_value);
          if (extracted) {
            value = extracted.extractedValue;
            placeholder = field_value;
            placeholderKey = extracted.placeholderKey;
          }
        } else if (hasValue(default_value) && !hasValue(field_value)) {
          const defaultObj = parseIfString(default_value);
          placeholderKey = findPlaceholderKey(defaultObj);
          placeholder = default_value;
          value = "";
        }
      }

      const baseProperty = {
        label: key_title,
        value,
        requirementType: requirement_type,
        helperText: ["required", "conditional_requirement"].includes(
          requirement_type,
        )
          ? `${key_title} is required.`
          : "",
        description,
        key_name,
        key_type,
        property_target,
      };

      // Add placeholder fields for pattern type
      return key_type === "pattern"
        ? { ...baseProperty, placeholder, placeholderKey }
        : baseProperty;
    })
    .filter(Boolean);

  // Fetch brand backend requirements + properties (no legacy report re-fetch
  // — the calling screen already has the brand's display info in allBrandsData).
  const getBrandPropertiesAndRequirements = async () => {
    if (!oauthTokenHeader) return;

    if (!isValidParam(brandSharedUid) || !isValidParam(bkInterfaceSharedUid)) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Invalid brand configuration.",
          type: "error",
        }),
      );
      return;
    }

    dispatch(setBrandPropertiesLoader(true));
    try {
      await Promise.all([
        BrandsEndPointLogic.getBrandBackendRequirements(
          oauthTokenHeader,
          bkInterfaceSharedUid,
        ),
        BrandsEndPointLogic.getBrandProperties(
          oauthTokenHeader,
          brandSharedUid,
        ),
      ]);
    } catch (err) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch brand configuration report. Please try again.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setBrandPropertiesLoader(false));
    }
  };

  async function brandIntegrationProcess(brandId: any) {
    dispatch(setBrandPropertiesLoader(true));
    try {
      const data = {
        brand_id: brandId,
        override_configuration: false,
      };

      const res = await BrandsEndPointLogic.postBrandConfigurationProcess(
        oauthTokenHeader,
        data,
      );

      if (res?.status === 200) {
        // Refresh the Brands query so the row's updated status/tab
        // membership shows up — replaces the legacy report/PIE-chart refresh.
        await fetchedBrandsData();
        const successMessage =
          res?.data?.message ||
          "Brand Integration Process has been successfully initiated.";

        dispatch(
          setDisplayErrorSnackBar({
            message: successMessage,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Failed to process brand integration.",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to process brand integration.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setBrandPropertiesLoader(false));
    }
  }

  const fetchedTestAgainPopupdata = async (
    brandId: string | any,
    websiteSharedUid: string | any,
  ) => {
    dispatch(setIsFetchedPopupData(false));
    try {
      await BrandsEndPointLogic.getTestAgainPopupData(
        oauthTokenHeader,
        QUERY_IDS.POPUPDATA,
        websiteSharedUid,
        type,
        brandId,
      );
      return true;
    } catch (e) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to process brand integration.",
          type: "error",
        }),
      );
      return false;
    } finally {
      dispatch(setIsFetchedPopupData(true));
    }
  };

  return {
    formattedBrandProperties,
    getBrandPropertiesAndRequirements,
    brandIntegrationProcess,
    fetchedTestAgainPopupdata,
  };
};
